import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import AdminLayout from '../../components/AdminLayout';
import {
  FiDollarSign, FiClock, FiCheck, FiX, FiRefreshCw, FiDownload, FiSearch,
  FiEye, FiCheckCircle, FiXCircle, FiAlertCircle, FiCalendar, FiUser,
  FiCreditCard, FiHash, FiClipboard, FiChevronUp, FiChevronDown
} from 'react-icons/fi';

// Import shared admin components
import {
  AdminContainer, AdminHeader, HeaderContent, HeaderText, HeaderActions,
  ActionButton, MainContent, StatsGrid, StatCard, StatHeader, StatIcon,
  StatValue, StatLabel, SearchAndFilters, SearchBox, FilterSelect,
  ActionButtonGroup, DataTable, TableActionButton, Modal, ModalContent,
  ModalActions, ResultsCounter, LoadingSpinner, ErrorMessage
} from '../../styles/AdminComponents';

// Modal form specific styled components
import styled from 'styled-components';

const ModalFormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #374151;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  input, textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    font-size: 1rem;
    
    &:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
  }
  
  textarea {
    resize: vertical;
    min-height: 100px;
  }
  
  input[type="text"] {
    font-family: 'Monaco', 'Consolas', monospace;
  }
`;

// Enhanced payout card for mobile
const PayoutCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  
  .payout-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1rem;
    gap: 1rem;
    
    .payout-info {
      flex: 1;
      min-width: 0;
      
      .referrer-name {
        font-size: 1.1rem;
        font-weight: 600;
        color: #111827;
        margin-bottom: 0.25rem;
      }
      
      .referrer-id {
        font-size: 0.8rem;
        font-family: 'Monaco', 'Menlo', monospace;
        background: #f3f4f6;
        padding: 0.25rem 0.5rem;
        border-radius: 0.25rem;
        color: #6b7280;
        display: inline-block;
      }
    }
    
    .actions {
      display: flex;
      gap: 0.5rem;
      flex-shrink: 0;
      flex-wrap: wrap;
    }
  }
  
  .payout-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1rem;
    
    @media (max-width: 480px) {
      grid-template-columns: 1fr;
      gap: 0.75rem;
    }
    
    .detail-item {
      .label {
        font-size: 0.75rem;
        font-weight: 600;
        color: #6b7280;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        margin-bottom: 0.25rem;
      }
      
      .value {
        font-size: 0.9rem;
        color: #111827;
        font-weight: 500;
        
        &.amount {
          font-size: 1.1rem;
          font-weight: 700;
          color: #10b981;
        }
        
        &.address {
          font-family: 'Monaco', 'Consolas', monospace;
          font-size: 0.8rem;
          background: #f3f4f6;
          padding: 0.25rem 0.5rem;
          border-radius: 0.25rem;
          word-break: break-all;
        }
      }
    }
  }
`;

// Responsive container that switches between table and cards
const ResponsiveContainer = styled.div`
  .desktop-view {
    display: block;
    
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  .mobile-view {
    display: none;
    
    @media (max-width: 768px) {
      display: block;
    }
  }
`;

// Enhanced modal for payout details
const PayoutDetailsModal = styled(Modal)`
  .modal-content {
    max-width: 700px;
    max-height: 90vh;
    overflow-y: auto;
  }
`;

const PayoutDetailSection = styled.div`
  background: #f8fafc;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  
  h4 {
    font-size: 0.9rem;
    font-weight: 600;
    color: #374151;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 0.75rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .detail-grid {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 0.5rem 1rem;
    align-items: start;
    
    .detail-label {
      font-weight: 500;
      color: #6b7280;
      white-space: nowrap;
    }
    
    .detail-value {
      color: #111827;
      
      &.amount {
        font-size: 1.2rem;
        font-weight: 700;
        color: #10b981;
      }
      
      &.address {
        font-family: 'Monaco', 'Consolas', monospace;
        font-size: 0.9rem;
        background: white;
        padding: 0.5rem;
        border-radius: 0.25rem;
        word-break: break-all;
        border: 1px solid #e5e7eb;
      }
      
      &.notes {
        background: white;
        padding: 0.75rem;
        border-radius: 0.25rem;
        border: 1px solid #e5e7eb;
        font-style: italic;
      }
    }
  }
`;

const PayoutsPage = () => {
  const [payouts, setPayouts] = useState([]);
  const [filteredPayouts, setFilteredPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [payoutStats, setPayoutStats] = useState(null);

  // Modal states
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [txid, setTxid] = useState('');

  const adminRank = localStorage.getItem('adminRank');

  useEffect(() => {
    if (adminRank === 'owner') {
      fetchPayouts();
    }
  }, [adminRank]);

  useEffect(() => {
    const filtered = payouts.filter(payout => {
      const statusMatch = statusFilter === 'all' || payout.status === statusFilter;
      const searchMatch = !searchTerm || 
        (payout.referrer_id && payout.referrer_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (payout.referrer_name && payout.referrer_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (payout.payment_method && payout.payment_method.toLowerCase().includes(searchTerm.toLowerCase()));
      
      return statusMatch && searchMatch;
    });

    setFilteredPayouts(filtered);
  }, [payouts, searchTerm, statusFilter]);

  const fetchPayouts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      
      const res = await axios.get('/api/admin/payouts', { headers });
      
      if (res.data.success) {
        const payoutsData = res.data.payouts || [];
        setPayouts(payoutsData);
        
        // Calculate comprehensive payout stats
        const stats = {
          total_requests: payoutsData.length,
          pending_payouts: payoutsData.filter(p => p.status === 'pending').length,
          completed_payouts: payoutsData.filter(p => p.status === 'completed').length,
          rejected_payouts: payoutsData.filter(p => p.status === 'rejected').length,
          total_amount_paid: payoutsData
            .filter(p => p.status === 'completed')
            .reduce((sum, p) => sum + (p.amount || 0), 0),
          total_pending_amount: payoutsData
            .filter(p => p.status === 'pending')
            .reduce((sum, p) => sum + (p.amount || 0), 0),
          avg_payout_amount: payoutsData.length > 0 
            ? payoutsData.reduce((sum, p) => sum + (p.amount || 0), 0) / payoutsData.length 
            : 0,
          recent_requests: payoutsData.filter(p => {
            const dayAgo = new Date();
            dayAgo.setDate(dayAgo.getDate() - 1);
            return new Date(p.created_at) > dayAgo;
          }).length
        };
        setPayoutStats(stats);
      } else {
        setError('Failed to fetch payouts');
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching payouts:', err);
      setError('Failed to fetch payouts');
      setLoading(false);
      toast.error('Failed to fetch payouts');
    }
  };

  const refreshPayouts = () => {
    fetchPayouts();
  };

  const exportPayouts = () => {
    const csvContent = [
      ['Referrer ID', 'Referrer Name', 'Amount', 'Payment Method', 'Status', 'Requested', 'Processed', 'Admin Notes'],
      ...filteredPayouts.map(payout => [
        payout.referrer_id || '',
        payout.referrer_name || '',
        payout.amount || 0,
        payout.payment_method || '',
        payout.status || '',
        payout.created_at || '',
        payout.processed_at || '',
        payout.admin_notes || ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'payouts_export.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleViewPayout = (payout) => {
    setSelectedPayout(payout);
    setRejectReason('');
    setTxid('');
    setShowPayoutModal(true);
  };

  const handleApprovePayout = async (payoutId) => {
    if (!txid.trim()) {
      toast.error('Please provide a transaction ID (TXID) before approving');
      return;
    }

    if (!window.confirm('Are you sure you want to approve this payout? This action cannot be undone.')) {
      return;
    }

    try {
      setProcessing(true);
      const token = localStorage.getItem('adminToken');
      const res = await axios.put(`/api/admin/payouts/${payoutId}`, {
        status: 'completed',
        txid: txid.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.success) {
        toast.success('Payout approved successfully!');
        fetchPayouts();
        setShowPayoutModal(false);
      } else {
        toast.error(res.data.message || 'Failed to approve payout');
      }
    } catch (err) {
      console.error('Error approving payout:', err);
      const errorMsg = err.response?.data?.message || 'Failed to approve payout';
      toast.error(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectPayout = async (payoutId) => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejecting this payout');
      return;
    }

    if (!window.confirm('Are you sure you want to reject this payout?')) {
      return;
    }

    try {
      setProcessing(true);
      const token = localStorage.getItem('adminToken');
      const res = await axios.put(`/api/admin/payouts/${payoutId}`, {
        status: 'rejected',
        admin_notes: rejectReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.success) {
        toast.success('Payout rejected successfully!');
        fetchPayouts();
        setShowPayoutModal(false);
      } else {
        toast.error(res.data.message || 'Failed to reject payout');
      }
    } catch (err) {
      console.error('Error rejecting payout:', err);
      const errorMsg = err.response?.data?.message || 'Failed to reject payout';
      toast.error(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { class: 'status-pending', icon: <FiClock /> },
      completed: { class: 'status-confirmed', icon: <FiCheckCircle /> },
      rejected: { class: 'status-expired', icon: <FiXCircle /> }
    };
    
    const badge = badges[status] || { class: 'status-pending', icon: <FiAlertCircle /> };
    
    return (
      <span className={`status-badge ${badge.class}`}>
        {badge.icon}
        {status}
      </span>
    );
  };

  if (adminRank !== 'owner') {
    return (
      <AdminLayout>
        <AdminContainer>
          <AdminHeader>
            <HeaderContent>
              <HeaderText>
                <h1>Access Denied</h1>
                <p>Only owners can manage payouts</p>
              </HeaderText>
            </HeaderContent>
          </AdminHeader>
        </AdminContainer>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <AdminContainer>
        <AdminHeader>
          <HeaderContent>
            <HeaderText>
              <h1>Payout Management</h1>
              <p>Review and process referrer payout requests</p>
            </HeaderText>
            <HeaderActions>
              <ActionButton onClick={refreshPayouts} variant="secondary">
                <FiRefreshCw />
                <span className="btn-text">Refresh</span>
              </ActionButton>
              <ActionButton onClick={exportPayouts} variant="secondary">
                <FiDownload />
                <span className="btn-text">Export CSV</span>
              </ActionButton>
            </HeaderActions>
          </HeaderContent>
        </AdminHeader>

        <MainContent>
          {/* Enhanced Payout Stats Section */}
          {payoutStats && (
            <StatsGrid>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiClipboard color="#3b82f6" /></StatIcon>
                  <h3>Total Requests</h3>
                </StatHeader>
                <StatValue>{payoutStats.total_requests}</StatValue>
                <StatLabel>All Payout Requests</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiClock color="#f59e0b" /></StatIcon>
                  <h3>Pending</h3>
                </StatHeader>
                <StatValue>{payoutStats.pending_payouts}</StatValue>
                <StatLabel>{formatCurrency(payoutStats.total_pending_amount)}</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiCheckCircle color="#10b981" /></StatIcon>
                  <h3>Completed</h3>
                </StatHeader>
                <StatValue>{payoutStats.completed_payouts}</StatValue>
                <StatLabel>Processed Successfully</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiXCircle color="#ef4444" /></StatIcon>
                  <h3>Rejected</h3>
                </StatHeader>
                <StatValue>{payoutStats.rejected_payouts}</StatValue>
                <StatLabel>Declined Requests</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiDollarSign color="#10b981" /></StatIcon>
                  <h3>Total Paid</h3>
                </StatHeader>
                <StatValue>{formatCurrency(payoutStats.total_amount_paid)}</StatValue>
                <StatLabel>Completed Payouts</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiCreditCard color="#8b5cf6" /></StatIcon>
                  <h3>Avg Payout</h3>
                </StatHeader>
                <StatValue>{formatCurrency(payoutStats.avg_payout_amount)}</StatValue>
                <StatLabel>Average Amount</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiCalendar color="#06b6d4" /></StatIcon>
                  <h3>Recent</h3>
                </StatHeader>
                <StatValue>{payoutStats.recent_requests}</StatValue>
                <StatLabel>Last 24 Hours</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiAlertCircle color="#f59e0b" /></StatIcon>
                  <h3>Processing</h3>
                </StatHeader>
                <StatValue>
                  {payoutStats.pending_payouts > 0 ? '🔔' : '✅'}
                </StatValue>
                <StatLabel>
                  {payoutStats.pending_payouts > 0 ? 'Needs Attention' : 'All Clear'}
                </StatLabel>
              </StatCard>
            </StatsGrid>
          )}

          <SearchAndFilters>
            <SearchBox>
              <FiSearch />
              <input
                type="text"
                placeholder="Search by referrer ID, name, or payment method..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchBox>
            <FilterSelect
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="rejected">Rejected</option>
            </FilterSelect>
          </SearchAndFilters>

          <ActionButtonGroup>
            <ResultsCounter>{filteredPayouts.length} payout requests found</ResultsCounter>
          </ActionButtonGroup>

          {loading ? (
            <LoadingSpinner />
          ) : error ? (
            <ErrorMessage>{error}</ErrorMessage>
          ) : (
            <ResponsiveContainer>
              {/* Desktop Table View */}
              <div className="desktop-view">
                <DataTable>
                  <thead>
                    <tr>
                      <th>Referrer</th>
                      <th>Amount</th>
                      <th>Payment Method</th>
                      <th>Status</th>
                      <th>Requested</th>
                      <th>Processed</th>
                      <th>Admin Notes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPayouts.length === 0 ? (
                      <tr>
                        <td colSpan="8" style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                          {searchTerm || statusFilter !== 'all' 
                            ? 'No payouts match your filters' 
                            : 'No payout requests found'}
                        </td>
                      </tr>
                    ) : (
                      filteredPayouts.map((payout, index) => (
                        <tr key={payout.id || index}>
                          <td>
                            <div>
                              <div style={{ fontWeight: '600', color: '#111827' }}>
                                {payout.referrer_name || payout.referrer_id}
                              </div>
                              {payout.referrer_name && payout.referrer_id && (
                                <div style={{ fontSize: '0.8rem', color: '#6b7280', fontFamily: 'Monaco, Consolas, monospace' }}>
                                  ID: {payout.referrer_id}
                                </div>
                              )}
                            </div>
                          </td>
                          <td>
                            <span style={{ 
                              fontSize: '1.1rem', 
                              fontWeight: '700', 
                              color: '#10b981' 
                            }}>
                              {formatCurrency(payout.amount)}
                            </span>
                          </td>
                          <td>
                            <div>
                              <div style={{ 
                                fontWeight: '500', 
                                textTransform: 'uppercase',
                                color: '#374151'
                              }}>
                                {payout.payment_method}
                              </div>
                              {payout.payment_details && (
                                <div style={{ 
                                  fontSize: '0.8rem', 
                                  color: '#6b7280',
                                  fontFamily: 'Monaco, Consolas, monospace',
                                  wordBreak: 'break-all'
                                }}>
                                  {payout.payment_details.length > 20 
                                    ? `${payout.payment_details.substring(0, 20)}...` 
                                    : payout.payment_details}
                                </div>
                              )}
                            </div>
                          </td>
                          <td>{getStatusBadge(payout.status)}</td>
                          <td style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                            <div>{formatDate(payout.created_at)}</div>
                            <div style={{ fontSize: '0.8rem' }}>
                              {new Date(payout.created_at).toLocaleTimeString()}
                            </div>
                          </td>
                          <td style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                            {payout.processed_at ? (
                              <>
                                <div>{formatDate(payout.processed_at)}</div>
                                <div style={{ fontSize: '0.8rem' }}>
                                  {new Date(payout.processed_at).toLocaleTimeString()}
                                </div>
                              </>
                            ) : '-'}
                          </td>
                          <td style={{ maxWidth: '200px' }}>
                            {payout.admin_notes ? (
                              <div style={{ 
                                fontSize: '0.85rem',
                                color: '#6b7280',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {payout.admin_notes}
                              </div>
                            ) : '-'}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <TableActionButton
                                onClick={() => handleViewPayout(payout)}
                                title="View Details"
                              >
                                <FiEye />
                              </TableActionButton>
                              {payout.status === 'pending' && (
                                <>
                                  <TableActionButton
                                    onClick={() => {
                                      setSelectedPayout(payout);
                                      setTxid('');
                                      setRejectReason('');
                                      setShowPayoutModal(true);
                                    }}
                                    title="Approve"
                                    style={{ background: '#10b981' }}
                                  >
                                    <FiCheckCircle />
                                  </TableActionButton>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </DataTable>
              </div>

              {/* Enhanced Mobile Card View */}
              <div className="mobile-view">
                {filteredPayouts.map((payout, index) => (
                  <PayoutCard key={payout.id || index}>
                    <div className="payout-header">
                      <div className="payout-info">
                        <div className="referrer-name">
                          {payout.referrer_name || payout.referrer_id}
                        </div>
                        {payout.referrer_name && payout.referrer_id && (
                          <div className="referrer-id">{payout.referrer_id}</div>
                        )}
                      </div>
                      <div className="actions">
                        <TableActionButton
                          onClick={() => handleViewPayout(payout)}
                          title="View Details"
                        >
                          <FiEye />
                        </TableActionButton>
                        {payout.status === 'pending' && (
                          <TableActionButton
                            onClick={() => {
                              setSelectedPayout(payout);
                              setTxid('');
                              setRejectReason('');
                              setShowPayoutModal(true);
                            }}
                            title="Process"
                            style={{ background: '#10b981' }}
                          >
                            <FiCheckCircle />
                          </TableActionButton>
                        )}
                      </div>
                    </div>
                    
                    <div className="payout-details">
                      <div className="detail-item">
                        <div className="label">Amount</div>
                        <div className="value amount">{formatCurrency(payout.amount)}</div>
                      </div>
                      <div className="detail-item">
                        <div className="label">Status</div>
                        <div className="value">{getStatusBadge(payout.status)}</div>
                      </div>
                      <div className="detail-item">
                        <div className="label">Payment Method</div>
                        <div className="value">{payout.payment_method?.toUpperCase()}</div>
                      </div>
                      <div className="detail-item">
                        <div className="label">Requested</div>
                        <div className="value">{formatDate(payout.created_at)}</div>
                      </div>
                    </div>

                    {payout.payment_details && (
                      <div className="payout-details">
                        <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
                          <div className="label">Wallet Address</div>
                          <div className="value address">
                            {payout.payment_details.length > 30 
                              ? `${payout.payment_details.substring(0, 30)}...` 
                              : payout.payment_details}
                          </div>
                        </div>
                      </div>
                    )}
                  </PayoutCard>
                ))}
              </div>
            </ResponsiveContainer>
          )}

          {/* Enhanced Payout Details Modal */}
          {showPayoutModal && selectedPayout && (
            <PayoutDetailsModal>
              <ModalContent className="modal-content">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                  <h2>💰 Payout Request Details</h2>
                  <button 
                    onClick={() => setShowPayoutModal(false)}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      fontSize: '1.5rem', 
                      cursor: 'pointer',
                      color: '#6b7280',
                      padding: '0.5rem',
                      borderRadius: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <FiX />
                  </button>
                </div>

                {/* Basic Information */}
                <PayoutDetailSection>
                  <h4><FiUser /> Referrer Information</h4>
                  <div className="detail-grid">
                    <div className="detail-label">Referrer:</div>
                    <div className="detail-value">
                      {selectedPayout.referrer_name || selectedPayout.referrer_id}
                    </div>
                    {selectedPayout.referrer_name && selectedPayout.referrer_id && (
                      <>
                        <div className="detail-label">Referrer ID:</div>
                        <div className="detail-value">
                          <code>{selectedPayout.referrer_id}</code>
                        </div>
                      </>
                    )}
                  </div>
                </PayoutDetailSection>

                {/* Payment Information */}
                <PayoutDetailSection>
                  <h4><FiDollarSign /> Payment Details</h4>
                  <div className="detail-grid">
                    <div className="detail-label">Amount:</div>
                    <div className="detail-value amount">
                      {formatCurrency(selectedPayout.amount)}
                    </div>
                    <div className="detail-label">Payment Method:</div>
                    <div className="detail-value">
                      {selectedPayout.payment_method?.toUpperCase()}
                    </div>
                    <div className="detail-label">Wallet Address:</div>
                    <div className="detail-value address">
                      {selectedPayout.payment_details}
                    </div>
                    <div className="detail-label">Status:</div>
                    <div className="detail-value">
                      {getStatusBadge(selectedPayout.status)}
                    </div>
                  </div>
                </PayoutDetailSection>

                {/* Timeline */}
                <PayoutDetailSection>
                  <h4><FiCalendar /> Timeline</h4>
                  <div className="detail-grid">
                    <div className="detail-label">Requested:</div>
                    <div className="detail-value">
                      {formatDateTime(selectedPayout.created_at)}
                    </div>
                    {selectedPayout.processed_at && (
                      <>
                        <div className="detail-label">Processed:</div>
                        <div className="detail-value">
                          {formatDateTime(selectedPayout.processed_at)}
                        </div>
                      </>
                    )}
                  </div>
                </PayoutDetailSection>

                {/* Admin Notes */}
                {selectedPayout.admin_notes && (
                  <PayoutDetailSection>
                    <h4><FiClipboard /> Admin Notes</h4>
                    <div className="detail-grid">
                      <div className="detail-label">Notes:</div>
                      <div className="detail-value notes">
                        {selectedPayout.admin_notes}
                      </div>
                    </div>
                  </PayoutDetailSection>
                )}

                {/* Processing Actions for Pending Payouts */}
                {selectedPayout.status === 'pending' && (
                  <>
                    <ModalFormGroup>
                      <label>
                        <FiHash />
                        Transaction ID (TXID) - Required for approval:
                      </label>
                      <input
                        type="text"
                        value={txid}
                        onChange={(e) => setTxid(e.target.value)}
                        placeholder="Enter cryptocurrency transaction ID (e.g., 7b1eabe235b6b6c4c5e8e...)..."
                      />
                      <small style={{ color: '#6b7280', marginTop: '0.25rem' }}>
                        Provide the blockchain transaction ID to complete this payout
                      </small>
                    </ModalFormGroup>

                    <ModalFormGroup>
                      <label>
                        <FiClipboard />
                        Rejection Reason (if rejecting):
                      </label>
                      <textarea
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Provide a reason for rejecting this payout request..."
                      />
                    </ModalFormGroup>

                    <ModalActions>
                      <ActionButton
                        onClick={() => setShowPayoutModal(false)}
                        variant="secondary"
                      >
                        Cancel
                      </ActionButton>
                      <ActionButton
                        onClick={() => handleRejectPayout(selectedPayout.id)}
                        variant="danger"
                        disabled={processing || !rejectReason.trim()}
                      >
                        <FiXCircle />
                        {processing ? 'Processing...' : 'Reject Payout'}
                      </ActionButton>
                      <ActionButton
                        onClick={() => handleApprovePayout(selectedPayout.id)}
                        disabled={processing || !txid.trim()}
                      >
                        <FiCheckCircle />
                        {processing ? 'Processing...' : 'Approve Payout'}
                      </ActionButton>
                    </ModalActions>
                  </>
                )}

                {/* Close Action for Non-Pending Payouts */}
                {selectedPayout.status !== 'pending' && (
                  <ModalActions>
                    <ActionButton onClick={() => setShowPayoutModal(false)}>
                      Close
                    </ActionButton>
                  </ModalActions>
                )}
              </ModalContent>
            </PayoutDetailsModal>
          )}
        </MainContent>
      </AdminContainer>
    </AdminLayout>
  );
};

export default PayoutsPage; 